import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Users, Copy, Check, Ticket } from "lucide-react";
import { toast } from "sonner";

const statusColor = {
  open: "bg-blue-50 text-blue-700 border-blue-200",
  in_progress: "bg-amber-50 text-amber-700 border-amber-200",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed: "bg-zinc-100 text-zinc-600 border-zinc-200",
};

export default function TeamPage() {
  const [data, setData] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/company"), api.get("/company/tickets")])
      .then(([c, t]) => {
        setData(c.data);
        setTickets(t.data);
      })
      .catch(() => toast.error("Could not load team data"))
      .finally(() => setLoading(false));
  }, []);

  const copyCode = () => {
    navigator.clipboard.writeText(data.company.code);
    setCopied(true);
    toast.success("Company code copied — share it with your team");
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return <div className="p-12 text-zinc-400 text-sm">Loading team…</div>;
  if (!data) return <div className="p-12 text-zinc-500">Team data unavailable.</div>;

  return (
    <div className="p-8 lg:p-12 max-w-6xl" data-testid="team-page">
      <div className="mb-10 fade-up">
        <div className="text-xs uppercase tracking-[0.3em] font-bold text-blue-600 mb-3">Company admin</div>
        <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl text-zinc-900">{data.company.name}</h1>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-10">
        <div className="bg-blue-600 text-white rounded-3xl p-7" data-testid="company-code-card">
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-blue-200 mb-3">Your company code</div>
          <div className="flex items-center gap-4 flex-wrap">
            <span className="font-display font-black text-3xl tracking-widest" data-testid="company-code-value">{data.company.code}</span>
            <button
              onClick={copyCode}
              className="inline-flex items-center gap-2 bg-white text-blue-700 font-bold px-4 py-2 rounded-full text-sm hover:bg-blue-50 transition"
              data-testid="copy-company-code"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <p className="text-blue-100 text-sm mt-4">
            Share this code with employees. They sign up choosing <b>"Join my team"</b> and enter it — their tickets show up here automatically.
          </p>
        </div>

        <div className="bg-white border border-zinc-200 rounded-3xl p-7" data-testid="team-members-card">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-4 h-4 text-zinc-400" />
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-zinc-500">Team members ({data.members.length})</div>
          </div>
          <div className="space-y-2.5 max-h-44 overflow-y-auto">
            {data.members.map((m) => (
              <div key={m.id} className="flex items-center gap-3" data-testid={`member-row-${m.id}`}>
                <div className="w-8 h-8 bg-zinc-900 text-white rounded-full flex items-center justify-center text-xs font-bold shrink-0">
                  {m.name?.[0]?.toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-zinc-900 truncate">{m.name}</div>
                  <div className="text-xs text-zinc-500 truncate">{m.email}</div>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${m.role === "owner" ? "bg-blue-50 text-blue-700" : "bg-zinc-100 text-zinc-500"}`}>
                  {m.role === "owner" ? "Admin" : "Employee"}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <h2 className="font-display font-bold text-lg md:text-lg text-zinc-900 mb-4">All team tickets ({tickets.length})</h2>
      {tickets.length === 0 ? (
        <div className="bg-white rounded-3xl border border-zinc-200 p-14 text-center" data-testid="team-tickets-empty">
          <Ticket className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
          <p className="text-zinc-500 text-sm">No tickets from your team yet. They'll appear here the moment anyone submits one.</p>
        </div>
      ) : (
        <div className="bg-white rounded-3xl border border-zinc-200 overflow-hidden" data-testid="team-tickets-list">
          {tickets.map((t) => (
            <Link
              key={t.id}
              to={`/app/tickets/${t.id}`}
              className="flex items-center gap-4 p-5 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 transition"
              data-testid={`team-ticket-row-${t.id}`}
            >
              <div className="w-9 h-9 bg-zinc-100 text-zinc-700 rounded-full flex items-center justify-center text-xs font-bold shrink-0">
                {t.user_name?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-zinc-900 truncate">{t.title}</div>
                <div className="flex items-center gap-3 text-xs text-zinc-500 mt-1">
                  <span className="font-semibold">{t.user_name}</span>
                  <span>·</span>
                  <span className="font-mono uppercase">{t.id.slice(0, 8)}</span>
                  <span>·</span>
                  <span>{t.category}</span>
                  <span>·</span>
                  <span>{new Date(t.created_at).toLocaleDateString()}</span>
                </div>
              </div>
              <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full border ${statusColor[t.status]}`}>
                {t.status.replace("_", " ")}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
